import * as React from 'react'

import { styled, Theme } from '~/styled'
import { useWindowScrollPosition } from '~/utilities/hooks/useWindowScrollPosition'

import { Divider } from '../divider/Divider'
import { colorSchemes as topBarColorSchemes } from './TopBar.colorscheme.config'
import { TopBarActionIcon } from './TopBarActionIcon'
import { TopBarBrand, TopBarBrandLogo, TopBarBrandName } from './TopBarBrand'

const TopBarDivider = () => (
  <Divider orientation="vertical" className="bg-divider h-6" />
)

const StyledRoot = styled(
  'header',
  {
    base: [
      'bg-(--background)',
      'sticky',
      'flex',
      'items-center',
      'z-15',
      'transition-shadow',
      'duration-200',
      'ease-out'
    ],
    variants: {
      hasScrolled: {
        true: ['shadow-[0px_4px_4px_-2px_rgba(31,31,31,0.1)]']
      },
      size: {
        md: ['h-16'],
        lg: ['h-24']
      },
      diffused: {
        true: [
          'supports-color-mix:bg-[color-mix(in_hsl,(--base-1)_70%,transparent)]',
          'supports-color-mix:backdrop-blur-sm'
        ]
      },
      appearance: {
        standard: [
          'w-screen',
          'max-w-full',
          'top-0',
          'border-b',
          'border-b-(--border-bottom)'
        ],
        rounded: [
          'w-full',
          'top-2',
          'p-2',
          'border',
          'border-white',
          'rounded-full'
        ]
      }
    }
  },
  {
    enabledResponsiveVariants: true
  }
)

const Container = styled('div', {
  base: ['flex', 'items-center', 'h-10', 'mx-4', 'w-full', 'md:mx-8']
})

type TopBarProps = React.ComponentProps<typeof StyledRoot> &
  React.ComponentProps<typeof Container> &
  React.ComponentProps<typeof Theme>

const TopBarComponent = ({
  size = 'md',
  diffused,
  theme,
  appearance = 'standard',
  ...rest
}: React.PropsWithChildren<TopBarProps>) => {
  const { y: scrollPositionY } = useWindowScrollPosition()

  return (
    <Theme theme={theme || topBarColorSchemes['light']}>
      <StyledRoot
        hasScrolled={!!scrollPositionY}
        diffused={diffused}
        appearance={appearance}
        size={size}
      >
        <Container {...rest} />
      </StyledRoot>
    </Theme>
  )
}

export const TopBar = Object.assign(TopBarComponent, {
  Brand: TopBarBrand,
  BrandLogo: TopBarBrandLogo,
  BrandName: TopBarBrandName,
  ActionIcon: TopBarActionIcon,
  Divider: TopBarDivider
})

TopBarComponent.displayName = 'TopBar'
